import { Component, EventEmitter, OnInit } from '@angular/core';
import {
  DwButtonCommand,
  DwButtonConfig,
  DwMessage,
  DwModalConfig,
  DwModalHandler,
  DwModalResult
} from '@devwareapps/devware-cap';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { StudentGroupSelectionValue } from '../../../shared/models/student-group-selection.model';
import { AssignmentStudentSelectionContext } from '../../models/assignment-student-management.model';
import { AssignmentStudentManagementService } from '../../services/assignment-student-management.service';

@Component({
  selector: 'app-assignment-add-students-modal',
  templateUrl: './assignment-add-students-modal.component.html',
  styleUrls: ['./assignment-add-students-modal.component.scss']
})
export class AssignmentAddStudentsModalComponent implements OnInit, DwModalHandler {
  modalConfig: DwModalConfig<number>;
  closeModal = new EventEmitter<DwModalResult<any>>();

  context: AssignmentStudentSelectionContext = null;
  selectionValue: StudentGroupSelectionValue = {
    studentIds: [],
    flightSchoolGroupIds: []
  };
  resolvedStudentIds: number[] = [];
  message: DwMessage = null;
  loading = true;
  saving = false;

  constructor(private assignmentStudentManagementService: AssignmentStudentManagementService) { }

  ngOnInit(): void {
    this.assignmentStudentManagementService.getSelectionContext(this.modalConfig.data)
      .pipe(catchError(() => of(null)))
      .subscribe(context => {
        this.loading = false;
        this.context = context;

        if (!context) {
          this.message = {
            messageHeading: 'Unable to load assignment',
            messageBody: 'The assignment student options could not be loaded. Please close this dialog and try again.'
          };
        }
      });
  }

  resolvedStudentIdsChanged(studentIds: number[]): void {
    this.resolvedStudentIds = [...(studentIds || [])];
    this.message = null;
  }

  buttonClick(buttonConfig: DwButtonConfig): Observable<DwModalResult> {
    if (buttonConfig.command === DwButtonCommand.cancel) {
      return of(this.createResult(true, true));
    }

    if (buttonConfig.command !== DwButtonCommand.ok
      && buttonConfig.command !== DwButtonCommand.save
      && buttonConfig.command !== DwButtonCommand.saveAndClose) {
      return of(this.createResult(false, false));
    }

    if (!this.resolvedStudentIds.length) {
      this.message = {
        messageHeading: 'Select students to add',
        messageBody: 'Choose at least one student who is not already on this assignment.'
      };
      return of(this.createResult(false, false));
    }

    this.saving = true;
    return this.assignmentStudentManagementService.addStudents(this.context.AssignmentId, this.resolvedStudentIds)
      .pipe(
        map(result => {
          this.saving = false;
          if (!result?.IsSuccess) {
            this.message = {
              messageHeading: 'Students could not be added',
              messageBody: result?.ErrorMessage || 'Please review the selected students and try again.'
            };
            return this.createResult(false, false);
          }

          const modalResult = this.createResult(true, false);
          modalResult.data = result;
          return modalResult;
        }),
        catchError(() => {
          this.saving = false;
          this.message = {
            messageHeading: 'Students could not be added',
            messageBody: 'An unexpected error occurred. Please try again.'
          };
          return of(this.createResult(false, false));
        })
      );
  }

  private createResult(closeModal: boolean, cancelled: boolean): DwModalResult<any> {
    const result = new DwModalResult<any>();
    result.closeModal = closeModal;
    result.cancelled = cancelled;
    return result;
  }
}
